import activeElement from 'dom-helpers/activeElement';
import contains from 'dom-helpers/query/contains';
import canUseDom from 'dom-helpers/util/inDOM';
import PropTypes from 'prop-types';
import componentOrElement from 'prop-types-extra/lib/componentOrElement';
import deprecated from 'prop-types-extra/lib/deprecated';
import elementType from 'prop-types-extra/lib/elementType';
import React, { cloneElement, useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import ReactDOM from 'react-dom';
import warning from 'warning';
import ModalManager from './ModalManager';
import addEventListener from './utils/addEventListener';
import addFocusListener from './utils/addFocusListener';

// Get container element without using findDOMNode
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
function getContainerElement(container, defaultContainer) {
  const element = typeof container === 'function' ? container() : container;

  // If element is already a DOM node, use it
  if (element && element.nodeType) {
    return element;
  }

  // Otherwise return the default container
  return defaultContainer;
}
const modalManager = new ModalManager();

/**
 * Love them or hate them, `<Modal/>` provides a solid foundation for creating dialogs, lightboxes, or whatever else.
 * The Modal component renders its `children` node in front of a backdrop component.
 *
 * The Modal offers a few helpful features over using just a `<Portal/>` component and some styles:
 *
 * - Manages dialog stacking when one-at-a-time just isn't enough.
 * - Creates a backdrop, for disabling interaction below the modal.
 * - It properly manages focus; moving to the modal content, and keeping it there until the modal is closed.
 * - It disables scrolling of the page content while open.
 * - Adds the appropriate ARIA roles are automatically.
 * - Easily pluggable animations via a `<Transition/>` component.
 *
 * Note that, in the same way the backdrop element prevents users from clicking or interacting
 * with the page content underneath the Modal, Screen readers also need to be signaled to not to
 * interact with page content while the Modal is open. To do this, we use a common technique of applying
 * the `aria-hidden='true'` attribute to the non-Modal elements in the Modal `container`. This means that for
 * a Modal to be truly modal, it should have a `container` that is _outside_ your app's
 * React hierarchy (such as the default: document.body).
 */
const Modal = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
    show = false,
    backdrop = true,
    keyboard = true,
    autoFocus: autoFocusProp = true,
    enforceFocus: enforceFocusProp = true,
    restoreFocus: restoreFocusProp = true,
    onHide = () => {},
    manager = modalManager,
    renderBackdrop = props => /*#__PURE__*/_jsx("div", {
      ...props
    }),
    container,
    onShow,
    onEscapeKeyDown,
    onEscapeKeyUp,
    onBackdropClick,
    backdropStyle,
    backdropClassName,
    containerClassName,
    transition: Transition,
    backdropTransition: BackdropTransition,
    onEnter,
    onEntering,
    onEntered,
    onExit,
    onExiting,
    onExited,
    onRendered,
    children,
    className,
    style,
    ...otherProps
  } = _ref;
  const [exited, setExited] = useState(!show);

  // Initialize portal container synchronously to avoid race conditions in tests
  const [portalContainer, setPortalContainer] = useState(() => {
    if (!canUseDom) {
      return null;
    }
    return getContainerElement(container, document.body);
  });

  // Refs for DOM elements
  const mountNodeRef = useRef(portalContainer);
  const modalNodeRef = useRef(null);
  const dialogRef = useRef(null);
  const backdropRef = useRef(null);

  // Refs for tracking state
  const lastFocusRef = useRef(null);
  const isMountedRef = useRef(false);
  const hasRenderedRef = useRef(false);

  // Refs for event listeners
  const onDocumentKeydownListenerRef = useRef(null);
  const onDocumentKeyupListenerRef = useRef(null);
  const onFocusinListenerRef = useRef(null);

  // Create a stable modal instance object that ModalManager can reference
  const modalInstanceRef = useRef({
    get modalNode() {
      return modalNodeRef.current;
    },
    get dialog() {
      return dialogRef.current;
    },
    get backdrop() {
      return backdropRef.current;
    }
  });

  // Update portal container when container prop changes
  useEffect(() => {
    if (!canUseDom) {
      return;
    }
    const containerElement = getContainerElement(container, document.body);
    setPortalContainer(containerElement);
    mountNodeRef.current = containerElement;
  }, [container]);

  // Call onRendered callback after portal renders
  useEffect(() => {
    if (portalContainer && !hasRenderedRef.current) {
      hasRenderedRef.current = true;
      if (onRendered) {
        onRendered();
      }
    }
  }, [portalContainer, onRendered]);
  const omitProps = useCallback(props => {
    // List of props that should not be passed to DOM elements
    const modalSpecificProps = ['show', 'container', 'onShow', 'renderBackdrop', 'onEscapeKeyDown', 'onEscapeKeyUp', 'onBackdropClick', 'backdropStyle', 'backdropClassName', 'containerClassName', 'keyboard', 'transition', 'backdropTransition', 'enforceFocus', 'restoreFocus', 'onEnter', 'onEntering', 'onEntered', 'onExit', 'onExiting', 'onExited', 'onHide', 'backdrop', 'manager', 'autoFocus', 'onRendered'];
    const keys = Object.keys(props);
    const newProps = {};
    keys.forEach(prop => {
      // Skip modal-specific props
      if (modalSpecificProps.includes(prop)) {
        return;
      }
      newProps[prop] = props[prop];
    });
    return newProps;
  }, []);
  const getDialogElement = useCallback(() => dialogRef.current, []);
  const isTopModal = useCallback(() => manager.isTopModal(modalInstanceRef.current), [manager]);

  // Expose methods through ref for parent components (like bootstrap Modal wrapper)
  useImperativeHandle(ref, () => ({
    getDialogElement,
    get modalNode() {
      return modalNodeRef.current;
    },
    get dialog() {
      return dialogRef.current;
    }
  }), [getDialogElement]);
  const checkForFocus = useCallback(() => {
    if (canUseDom) {
      lastFocusRef.current = activeElement();
    }
  }, []);
  const restoreLastFocus = useCallback(() => {
    // Support: <=IE11 doesn't support `focus()` on svg elements (RB: #917)
    if (lastFocusRef.current && lastFocusRef.current.focus) {
      lastFocusRef.current.focus();
      lastFocusRef.current = null;
    }
  }, []);
  const autoFocusFunc = useCallback(() => {
    if (!autoFocusProp) {
      return;
    }
    const dialogElement = getDialogElement();
    const doc = mountNodeRef.current ? mountNodeRef.current.ownerDocument : document;
    const currentActiveElement = activeElement(doc);
    if (dialogElement && !contains(dialogElement, currentActiveElement)) {
      lastFocusRef.current = currentActiveElement;
      if (!dialogElement.hasAttribute('tabIndex')) {
        process.env.NODE_ENV !== "production" ? warning(false, 'The modal content node does not accept focus. For the benefit of ' + 'assistive technologies, the tabIndex of the node is being set ' + 'to "-1".') : void 0;
        dialogElement.setAttribute('tabIndex', -1);
      }
      dialogElement.focus();
    }
  }, [autoFocusProp, getDialogElement]);
  const enforceFocusFunc = useCallback(() => {
    if (!enforceFocusProp || !isMountedRef.current || !isTopModal()) {
      return;
    }
    const dialogElement = getDialogElement();
    const doc = mountNodeRef.current ? mountNodeRef.current.ownerDocument : document;
    const currentActiveElement = activeElement(doc);
    if (dialogElement && !contains(dialogElement, currentActiveElement)) {
      dialogElement.focus();
    }
  }, [enforceFocusProp, isTopModal, getDialogElement]);
  const handleBackdropClick = useCallback(e => {
    if (e.target !== e.currentTarget) {
      return;
    }
    if (onBackdropClick) {
      onBackdropClick(e);
    }
    if (backdrop === true) {
      onHide();
    }
  }, [onBackdropClick, backdrop, onHide]);
  const handleDocumentKeyDown = useCallback(e => {
    if (keyboard && e.keyCode === 27 && isTopModal()) {
      if (onEscapeKeyDown) {
        onEscapeKeyDown(e);
      }
      onHide();
    }
  }, [keyboard, isTopModal, onEscapeKeyDown, onHide]);
  const handleDocumentKeyUp = useCallback(e => {
    if (keyboard && e.keyCode === 27 && isTopModal()) {
      if (onEscapeKeyUp) {
        onEscapeKeyUp(e);
      }
    }
  }, [keyboard, isTopModal, onEscapeKeyUp]);
  const onShowFunc = useCallback(() => {
    const doc = mountNodeRef.current ? mountNodeRef.current.ownerDocument : document;
    const containerElement = getContainerElement(container, doc.body);
    manager.add(modalInstanceRef.current, containerElement, containerClassName);
    onDocumentKeydownListenerRef.current = addEventListener(doc, 'keydown', handleDocumentKeyDown);
    onDocumentKeyupListenerRef.current = addEventListener(doc, 'keyup', handleDocumentKeyUp);
    onFocusinListenerRef.current = addFocusListener(enforceFocusFunc);
  }, [container, containerClassName, manager, handleDocumentKeyDown, handleDocumentKeyUp, enforceFocusFunc]);
  const onHideFunc = useCallback(() => {
    manager.remove(modalInstanceRef.current);
    if (onDocumentKeydownListenerRef.current) {
      onDocumentKeydownListenerRef.current.remove();
    }
    if (onDocumentKeyupListenerRef.current) {
      onDocumentKeyupListenerRef.current.remove();
    }
    if (onFocusinListenerRef.current) {
      onFocusinListenerRef.current.remove();
    }
    if (restoreFocusProp) {
      restoreLastFocus();
    }
  }, [manager, restoreFocusProp, restoreLastFocus]);
  const handleHidden = useCallback(function () {
    setExited(true);
    onHideFunc();
    if (onExited) {
      onExited(...arguments);
    }
  }, [onHideFunc, onExited]);
  const onPortalRendered = useCallback(() => {
    autoFocusFunc();
    if (onShow) {
      onShow();
    }
  }, [autoFocusFunc, onShow]);

  // Call onPortalRendered when modal mounts and is shown
  useEffect(() => {
    if (portalContainer && show && modalNodeRef.current) {
      onPortalRendered();
    }
  }, [portalContainer, show, onPortalRendered]);

  // Effect to handle show/hide state changes
  useEffect(() => {
    if (show) {
      setExited(false);
    } else if (!Transition) {
      setExited(true);
    }
  }, [show, Transition]);

  // Effect to handle focus check when opening
  useEffect(() => {
    if (show) {
      checkForFocus();
    }
  }, [show, checkForFocus]);

  // Effect for mount/unmount and show/hide logic
  useEffect(() => {
    isMountedRef.current = true;
    if (show) {
      onShowFunc();
    }
    return () => {
      isMountedRef.current = false;
      if (show || Transition && !exited) {
        onHideFunc();
      }
    };
  }, [show, Transition, exited, onShowFunc, onHideFunc]);

  // Effect to handle show/hide transitions
  useEffect(() => {
    const prevShowRef = {
      current: show
    };
    return () => {
      const prevShow = prevShowRef.current;
      if (prevShow && !show && !Transition) {
        onHideFunc();
      } else if (!prevShow && show) {
        onShowFunc();
      }
    };
  }, [show, Transition, onShowFunc, onHideFunc]);
  const setModalNodeRef = useCallback(newRef => {
    modalNodeRef.current = newRef;
  }, []);
  const setDialogRef = useCallback(newRef => {
    dialogRef.current = newRef;
  }, []);
  const renderBackdropFunc = useCallback(() => {
    const backdropRefFunc = newRef => {
      backdropRef.current = newRef;
    };
    let backdropElement = renderBackdrop({
      ref: backdropRefFunc,
      style: backdropStyle,
      className: backdropClassName,
      onClick: handleBackdropClick
    });
    if (BackdropTransition) {
      backdropElement = /*#__PURE__*/_jsx(BackdropTransition, {
        appear: true,
        in: show,
        children: backdropElement
      });
    }
    return backdropElement;
  }, [renderBackdrop, backdropStyle, backdropClassName, handleBackdropClick, BackdropTransition, show]);

  // Build the props object including all otherProps
  const allProps = {
    show,
    container,
    onShow,
    renderBackdrop,
    onEscapeKeyDown,
    onEscapeKeyUp,
    onBackdropClick,
    backdropStyle,
    backdropClassName,
    containerClassName,
    keyboard,
    transition: Transition,
    backdropTransition: BackdropTransition,
    enforceFocus: enforceFocusProp,
    restoreFocus: restoreFocusProp,
    onEnter,
    onEntering,
    onEntered,
    onExit,
    onExiting,
    onExited,
    onHide,
    backdrop,
    manager,
    autoFocus: autoFocusProp,
    ...otherProps
  };
  let dialog = React.Children.only(children);
  const filteredProps = omitProps(allProps);
  const mountModal = show || Transition && !exited;
  if (!mountModal || !portalContainer) {
    return null;
  }
  const {
    role,
    tabIndex
  } = dialog.props;
  if (role === undefined || tabIndex === undefined) {
    dialog = /*#__PURE__*/cloneElement(dialog, {
      role: role === undefined ? 'document' : role,
      tabIndex: tabIndex == null ? '-1' : tabIndex
    });
  }
  if (Transition) {
    dialog = /*#__PURE__*/_jsx(Transition, {
      appear: true,
      unmountOnExit: true,
      in: show,
      onExit: onExit,
      onExiting: onExiting,
      onExited: handleHidden,
      onEnter: onEnter,
      onEntering: onEntering,
      onEntered: onEntered,
      children: dialog
    });
  }
  const modalContent = /*#__PURE__*/_jsxs("div", {
    ref: setModalNodeRef,
    role: role || 'dialog',
    ...filteredProps,
    style: style,
    className: className,
    children: [backdrop && renderBackdropFunc(), /*#__PURE__*/_jsx("div", {
      ref: setDialogRef,
      children: dialog
    })]
  });
  return /*#__PURE__*/ReactDOM.createPortal(modalContent, portalContainer);
});
Modal.displayName = 'Modal';
Modal.propTypes = {
  onRendered: PropTypes.func,
  /**
   * Set the visibility of the Modal
   */
  show: PropTypes.bool,
  /**
   * A Node, Component instance, or function that returns either. The Modal is appended to it's container element.
   *
   * For the sake of assistive technologies, the container should usually be the document body, so that the rest of the
   * page content can be placed behind a virtual backdrop as well as a visual one.
   */
  container: PropTypes.oneOfType([componentOrElement, PropTypes.func]),
  /**
   * A callback fired when the Modal is opening.
   */
  onShow: PropTypes.func,
  /**
   * A callback fired when either the backdrop is clicked, or the escape key is pressed.
   *
   * The `onHide` callback only signals intent from the Modal,
   * you must actually set the `show` prop to `false` for the Modal to close.
   */
  onHide: PropTypes.func,
  /**
   * Include a backdrop component.
   */
  backdrop: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf(['static'])]),
  /**
   * A function that returns a backdrop component. Useful for custom
   * backdrop rendering.
   *
   * ```js
   *  renderBackdrop={props => <MyBackdrop {...props} />}
   * ```
   */
  renderBackdrop: PropTypes.func,
  /**
   * A callback fired when the escape key, if specified in `keyboard`, is pressed.
   */
  onEscapeKeyDown: PropTypes.func,
  /**
   * Support for this function will be deprecated. Please use `onEscapeKeyDown` instead
   * A callback fired when the escape key, if specified in `keyboard`, is pressed.
   * @deprecated
   */
  onEscapeKeyUp: deprecated(PropTypes.func, 'Please use onEscapeKeyDown instead for consistency'),
  /**
   * A callback fired when the backdrop, if specified, is clicked.
   */
  onBackdropClick: PropTypes.func,
  /**
   * A style object for the backdrop component.
   */
  backdropStyle: PropTypes.object,
  /**
   * A css class or classes for the backdrop component.
   */
  backdropClassName: PropTypes.string,
  /**
   * A css class or set of classes applied to the modal container when the modal is open,
   * and removed when it is closed.
   */
  containerClassName: PropTypes.string,
  /**
   * Close the modal when escape key is pressed
   */
  keyboard: PropTypes.bool,
  /**
   * A `react-transition-group@2.0.0` `<Transition/>` component used
   * to control animations for the dialog component.
   */
  transition: elementType,
  /**
   * A `react-transition-group@2.0.0` `<Transition/>` component used
   * to control animations for the backdrop components.
   */
  backdropTransition: elementType,
  /**
   * When `true` The modal will automatically shift focus to itself when it opens, and
   * replace it to the last focused element when it closes. This also
   * works correctly with any Modal children that have the `autoFocus` prop.
   *
   * Generally this should never be set to `false` as it makes the Modal less
   * accessible to assistive technologies, like screen readers.
   */
  autoFocus: PropTypes.bool,
  /**
   * When `true` The modal will prevent focus from leaving the Modal while open.
   *
   * Generally this should never be set to `false` as it makes the Modal less
   * accessible to assistive technologies, like screen readers.
   */
  enforceFocus: PropTypes.bool,
  /**
   * When `true` The modal will restore focus to previously focused element once
   * modal is hidden
   */
  restoreFocus: PropTypes.bool,
  /**
   * Callback fired before the Modal transitions in
   */
  onEnter: PropTypes.func,
  /**
   * Callback fired as the Modal begins to transition in
   */
  onEntering: PropTypes.func,
  /**
   * Callback fired after the Modal finishes transitioning in
   */
  onEntered: PropTypes.func,
  /**
   * Callback fired right before the Modal transitions out
   */
  onExit: PropTypes.func,
  /**
   * Callback fired as the Modal begins to transition out
   */
  onExiting: PropTypes.func,
  /**
   * Callback fired after the Modal finishes transitioning out
   */
  onExited: PropTypes.func,
  /**
   * A ModalManager instance used to track and manage the state of open
   * Modals. Useful when customizing how modals interact within a container
   */
  manager: PropTypes.object
};
Modal.Manager = ModalManager;
export default Modal;